/**
 * AIAssetsSubTab V2
 *
 * Zeigt alle KI-generierten Bilder und Assets mit Metadaten.
 * - Statistics Cards
 * - Pending Images Grid
 * - Assets Table mit Approve/Reject
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bot, Clock, CheckCircle, Image as ImageIcon, Check, X } from 'lucide-react';
import {
  getProductAIGeneratedImages,
  approveProductImage,
  rejectProductImage,
} from '@/lib/api/product-images';
import type {
  JunctionLink,
  GeneratedImageWithMetadata,
  ProductAssetStats,
} from '@/types/product-images';
import {
  Button,
  Badge,
  Card,
  CardContent,
  Loading,
  EmptyState,
  Alert,
  AlertDescription,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/v2/ui';

interface AIAssetsSubTabProps {
  productId: string;
}

export function AIAssetsSubTab({ productId }: AIAssetsSubTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Daten laden
  const { data, isLoading, error } = useQuery({
    queryKey: ['product-ai-images', productId],
    queryFn: () => getProductAIGeneratedImages(productId, { includePendingImages: true }),
  });

  // Approve Mutation
  const approveMutation = useMutation({
    mutationFn: ({ junctionId }: { junctionId: string }) =>
      approveProductImage(productId, junctionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-ai-images', productId] });
      toast.success(t('toast.success.assetApproved'));
    },
    onError: () => {
      toast.error('Fehler beim Freigeben');
    },
  });

  // Reject Mutation
  const rejectMutation = useMutation({
    mutationFn: ({ junctionId }: { junctionId: string }) =>
      rejectProductImage(productId, junctionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-ai-images', productId] });
      toast.success(t('toast.success.assetRejected'));
    },
    onError: () => {
      toast.error('Fehler beim Ablehnen');
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loading size="lg" />
        <p className="mt-4 text-neutral-600 dark:text-neutral-400">Lade KI-generierte Assets...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        <AlertDescription>Fehler beim Laden der KI-Assets</AlertDescription>
      </Alert>
    );
  }

  const assets = data?.assets || [];
  const pendingImages = data?.pending_images || [];
  const stats = data?.stats as ProductAssetStats | undefined;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <Check className="h-3 w-3" />
            Freigegeben
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="error" className="flex items-center gap-1">
            <X className="h-3 w-3" />
            Abgelehnt
          </Badge>
        );
      case 'pending':
      default:
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {t('common.pending')}
          </Badge>
        );
    }
  };

  const getAssetTypeBadge = (type: string) => {
    switch (type) {
      case 'AI_GENERATED':
        return <Badge variant="info">KI-generiert</Badge>;
      case 'MASTER':
        return <Badge variant="primary">Master</Badge>;
      case 'VARIANT':
        return <Badge variant="secondary">Variante</Badge>;
      case 'REFERENCE':
        return <Badge variant="secondary">Referenz</Badge>;
      default:
        return <Badge variant="secondary">{type}</Badge>;
    }
  };

  return (
    <div>
      {/* Statistiken */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {stats.total}
              </div>
              <small className="text-neutral-600 dark:text-neutral-400">Gesamt</small>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {stats.ai_generated}
              </div>
              <small className="text-neutral-600 dark:text-neutral-400">KI-generiert</small>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.approved}
              </div>
              <small className="text-neutral-600 dark:text-neutral-400">Freigegeben</small>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.pending_images}
              </div>
              <small className="text-neutral-600 dark:text-neutral-400">Ausstehend</small>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Images (noch nicht überführt) */}
      {pendingImages.length > 0 && (
        <div className="mb-6">
          <h6 className="flex items-center gap-2 mb-3 text-lg font-semibold text-neutral-900 dark:text-neutral-50">
            <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            {t('common.pendingImages', { count: pendingImages.length })}
          </h6>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {pendingImages.map((image: GeneratedImageWithMetadata) => (
              <Card key={image.id}>
                <div className="aspect-square bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                  {image.image_url ? (
                    <img
                      src={`/api/generated/${image.image_url}`}
                      alt={image.prompt}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="h-12 w-12 text-neutral-400" />
                  )}
                </div>
                <CardContent className="p-2">
                  <div className="text-sm truncate" title={image.prompt}>
                    {image.prompt || '(Kein Prompt)'}
                  </div>
                  <div className="flex gap-1 mt-1">
                    <Badge variant="secondary">{image.ratio}</Badge>
                    <Badge variant="info">{image.preset_type}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Überführte Assets */}
      <h6 className="flex items-center gap-2 mb-3 text-lg font-semibold text-neutral-900 dark:text-neutral-50">
        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
        Produkt-Assets ({assets.length})
      </h6>

      {assets.length === 0 && pendingImages.length === 0 ? (
        <EmptyState
          icon={<Bot className="h-12 w-12" />}
          title="Keine KI-Assets"
          description="Generieren Sie Bilder über die Galerie und übertragen Sie diese hierher."
        />
      ) : assets.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Bild</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Preset</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead>Erstellt</TableHead>
                  <TableHead className="w-24">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((link: JunctionLink) => {
                  const asset = link.workspace_product_assets;
                  return (
                    <TableRow key={link.id}>
                      <TableCell>
                        <div className="w-12 h-12 bg-neutral-100 dark:bg-neutral-800 rounded flex items-center justify-center overflow-hidden">
                          {asset?.generated_image_id ? (
                            <img
                              src={`/image/${asset.generated_image_id}`}
                              alt=""
                              className="w-full h-full object-contain"
                            />
                          ) : asset?.s3_key_original ? (
                            <img
                              src={`/api/v1/products/asset/${link.asset_id}/image`}
                              alt=""
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <ImageIcon className="h-6 w-6 text-neutral-400" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getAssetTypeBadge(link.asset_type)}</TableCell>
                      <TableCell>
                        {link.preset_type ? (
                          <Badge variant="secondary">{link.preset_type}</Badge>
                        ) : (
                          <span className="text-neutral-600 dark:text-neutral-400">–</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(link.approval_status)}</TableCell>
                      <TableCell className="text-sm text-neutral-600 dark:text-neutral-400">
                        {new Date(link.created_at).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {link.approval_status !== 'approved' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => approveMutation.mutate({ junctionId: link.id })}
                              disabled={approveMutation.isPending}
                              title={t('common.approve')}
                              aria-label={t('common.approve')}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          {link.approval_status !== 'rejected' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => rejectMutation.mutate({ junctionId: link.id })}
                              disabled={rejectMutation.isPending}
                              title={t('common.reject')}
                              aria-label={t('common.reject')}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
